import { X } from "lucide-react";
import styles from "./ProductDetails.module.scss";
import { formatNumberToPrice } from "../../../../helpers/priceFormatting";
import type { SalesProduct } from "../ProductsTable/types";
import type { StorageAddressStock } from "../ProductsTable/types";

interface ProductDetailsPanelProps {
  product: SalesProduct | null;
  isOpen: boolean;
  onClose: () => void;
}

export function ProductDetails({
  product,
  isOpen,
  onClose,
}: ProductDetailsPanelProps) {
  if (!isOpen || !product) return null;

  return (
    <div className={styles.detailsBackdrop} onClick={onClose}>
      <div
        className={styles.detailsPanel}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={`Detalles del producto ${product.name}`}
      >
        <div className={styles.detailsHeader}>
          <div className={styles.detailsTitleGroup}>
            <h3 className={styles.detailsTitle}>{product.name}</h3>
            <span className={styles.detailsSubtitle}>ID: {product.id}</span>
          </div>
          <button
            className={styles.closeButton}
            aria-label="Cerrar detalles"
            onClick={onClose}
          >
            <X className={styles.closeIcon} />
          </button>
        </div>
        <div className={styles.detailsContent}>
          {product.imageUrl ? (
            <div className={styles.imagePreview}>
              <img src={product.imageUrl} alt={product.name} />
            </div>
          ) : (
            <div className={styles.imagePreview}>
              <img src="/placeholder-product.png" alt="No preview available" />
            </div>
          )}

          <div className={styles.detailsRow}>
            <span className={styles.detailsLabel}>Price</span>
            <span className={styles.detailsValue}>
              {formatNumberToPrice(product.price)}
            </span>
          </div>

          {product.brand && (
            <div className={styles.detailsRow}>
              <span className={styles.detailsLabel}>Brand</span>
              <span className={styles.detailsValue}>{product.brand}</span>
            </div>
          )}

          {(product.widthCm || product.lengthCm) && (
            <div className={styles.detailsRow}>
              <span className={styles.detailsLabel}>Dimensions</span>
              <span className={styles.detailsValue}>
                {product.widthCm} × {product.lengthCm} cm
                {product.areaM2 ? ` (${product.areaM2} m²)` : ""}
              </span>
            </div>
          )}

          {/* Stock by location */}
          <div className={styles.detailsRow}>
            <span className={styles.detailsLabel}>Stock by location</span>
          </div>
          {product.storageAddresses && product.storageAddresses.length > 0 ? (
            product.storageAddresses.map((loc: StorageAddressStock) => (
              <div key={loc.address_id} className={styles.detailsRow}>
                <span className={styles.detailsLabel}>{loc.address_name}</span>
                <span className={styles.detailsValue}>{loc.quantity} units</span>
              </div>
            ))
          ) : (
            <div className={styles.detailsRow}>
              <span className={styles.detailsValue}>No stock locations registered</span>
            </div>
          )}

          {product.complementaries && product.complementaries.length > 0 && (
            <div className={styles.detailsRow}>
              <span className={styles.detailsLabel}>Complementarios</span>
              <ul className={styles.detailsValue}>
                {product.complementaries.map((c) => (
                  <li key={c.id}>
                    <span>{c.name}</span>
                    <span>
                      {" — "}
                      {c.unit === "m2"
                        ? `${c.quantity} m²`
                        : `${c.quantity} ${c.quantity === 1 ? "unidad" : "unidades"}`}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
